import { useEffect, useMemo, useState } from "react";
import { Copy, Eye, EyeOff, KeyRound, Plus, Search, Save, Trash2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";

const empty = { deploy_accounts: [], preview_accounts: [] };
const accountRow = (account = {}) => ({
    _key: account._key || globalThis.crypto?.randomUUID?.() || `credential_${Date.now()}_${Math.random()}`,
    username: account.username || "",
    password: account.password || "",
});
const blankAccount = () => accountRow();

export default function CredentialsDialog({ open, onOpenChange, scope }) {
    const { hasPermission } = useAuth();
    const canManage = hasPermission("service_credentials_manage");
    const [projects, setProjects] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [search, setSearch] = useState("");
    const [detail, setDetail] = useState(null);
    const [form, setForm] = useState(empty);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadList = async () => {
        const { data } = await api.get(`/service-credentials/${scope}`);
        setProjects(data || []);
        return data || [];
    };

    useEffect(() => {
        if (!open) return;
        setSearch(""); setSelectedId(""); setDetail(null); setEditing(false);
        loadList().catch((error)=>toast.error(formatApiError(error.response?.data?.detail)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, scope]);

    const visible = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return projects;
        return projects.filter((project) => [project.project_name, project.client_name, project.deployment_code].some((value)=>String(value||"").toLowerCase().includes(term)));
    }, [projects, search]);

    const selectProject = async (projectId) => {
        setSelectedId(projectId); setEditing(false);
        try {
            const { data } = await api.get(`/service-credentials/${scope}/${projectId}`);
            setDetail(data);
            setForm({
                deploy_accounts: (data.deploy_accounts || []).map(accountRow),
                preview_accounts: (data.preview_accounts || []).map(accountRow),
            });
        } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = Object.fromEntries(Object.entries(form).map(([field, accounts]) => [
                field,
                accounts.map(({username,password}) => ({username,password})),
            ]));
            const { data } = await api.put(`/service-credentials/${scope}/${selectedId}`, payload);
            setDetail(data);
            setForm({
                deploy_accounts: (data.deploy_accounts || []).map(accountRow),
                preview_accounts: (data.preview_accounts || []).map(accountRow),
            });
            setEditing(false); await loadList(); toast.success("Credenciales guardadas");
        } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
        finally { setSaving(false); }
    };

    const project = detail?.project;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-orange-600"/>Credenciales de servicios</DialogTitle></DialogHeader>
                <div className="grid min-h-0 flex-1 md:grid-cols-[300px_1fr] gap-4">
                    <aside className="min-h-0 rounded-lg border border-slate-200 overflow-hidden flex flex-col">
                        <div className="relative border-b p-2"><Search className="absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"/><Input value={search} onChange={(e)=>setSearch(e.target.value)} className="pl-9" placeholder="Buscar proyecto..."/></div>
                        <div className="min-h-0 flex-1 overflow-y-auto p-2 space-y-1">
                            {visible.map((item)=><button key={item.project_id} type="button" onClick={()=>selectProject(item.project_id)} className={`w-full rounded-md px-3 py-2 text-left text-sm ${selectedId===item.project_id?"bg-orange-50 text-orange-800":"hover:bg-slate-50"}`}>
                                <div className="font-medium truncate">{item.project_name}</div><div className="text-xs text-slate-500 truncate">{item.client_name || "Sin empresa"}</div><div className="mt-1 text-[10px] uppercase tracking-wide text-slate-400">{item.has_credentials ? "Credenciales registradas" : "Sin credenciales"}</div>
                            </button>)}
                            {!visible.length && <p className="p-4 text-center text-sm text-slate-400">Sin proyectos</p>}
                        </div>
                    </aside>
                    <section className="min-h-0 overflow-y-auto rounded-lg border border-slate-200 p-5">
                        {!detail && <div className="h-full min-h-[300px] flex items-center justify-center text-sm text-slate-400">Selecciona un proyecto para ver sus credenciales.</div>}
                        {detail && <div className="space-y-5">
                            <div><h3 className="font-semibold text-slate-900">{project?.project_name}</h3><p className="text-sm text-slate-500">{project?.client_name}{project?.deployment_code ? ` · ${project.deployment_code}` : ""}</p></div>
                            <CredentialBlock title="Deploy / Producción" accounts={form.deploy_accounts} editing={editing} onChange={(deploy_accounts)=>setForm({...form,deploy_accounts})}/>
                            <CredentialBlock title="Preview" accounts={form.preview_accounts} editing={editing} onChange={(preview_accounts)=>setForm({...form,preview_accounts})}/>
                            {canManage && <div className="flex justify-end gap-2">{editing ? <><Button variant="outline" onClick={()=>selectProject(selectedId)}>Cancelar</Button><Button onClick={save} disabled={saving}><Save className="mr-2 h-4 w-4"/>{saving?"Guardando…":"Guardar"}</Button></> : <Button onClick={()=>setEditing(true)}>Editar credenciales</Button>}</div>}
                        </div>}
                    </section>
                </div>
                <DialogFooter><Button variant="ghost" onClick={()=>onOpenChange(false)}>Cerrar</Button></DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CredentialBlock({ title, accounts, editing, onChange }) {
    const [visible, setVisible] = useState(new Set());
    const rows = editing && !accounts.length ? [blankAccount()] : accounts;
    const update = (index, field, value) => onChange(rows.map((account, rowIndex) => rowIndex === index ? {...account, [field]:value} : account));
    const remove = (index) => onChange(rows.filter((_, rowIndex) => rowIndex !== index));
    const copy = async (value, label) => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            toast.success(`${label} copiado`);
        } catch {
            toast.error(`No se pudo copiar ${label.toLowerCase()}`);
        }
    };

    return <div className="rounded-lg border border-slate-200 p-4 space-y-3">
        <div className="flex items-center justify-between gap-3"><h4 className="text-sm font-semibold text-slate-900">{title}</h4>{editing&&<Button type="button" variant="outline" size="sm" onClick={()=>onChange([...rows,blankAccount()])}><Plus className="mr-1.5 h-4 w-4"/>Agregar usuario</Button>}</div>
        {!rows.length&&<p className="rounded-md bg-slate-50 px-3 py-4 text-center text-sm text-slate-400">Sin credenciales</p>}
        {rows.map((account,index)=>{
            const show=visible.has(account._key);
            return <div key={account._key} className="rounded-md border border-slate-100 bg-slate-50/50 p-3 space-y-3">
                <div className="flex items-center justify-between"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Usuario {index+1}</span>{editing&&<Button type="button" variant="ghost" size="icon" onClick={()=>remove(index)} title="Eliminar usuario"><Trash2 className="h-4 w-4 text-red-500"/></Button>}</div>
                <div><Label>Usuario</Label><div className="mt-1 flex gap-2">{editing?<Input value={account.username||""} onChange={(event)=>update(index,"username",event.target.value)}/>:<div className="flex-1 rounded-md bg-white px-3 py-2 text-sm font-mono">{account.username||"—"}</div>}<CopyButton value={account.username} label="Usuario" onCopy={copy}/></div></div>
                <div><Label>Clave</Label><div className="mt-1 flex gap-2">{editing?<Input type={show?"text":"password"} value={account.password||""} onChange={(event)=>update(index,"password",event.target.value)}/>:<div className="flex-1 rounded-md bg-white px-3 py-2 text-sm font-mono">{account.password?(show?account.password:"••••••••••"):"—"}</div>}<Button type="button" variant="outline" size="icon" onClick={()=>setVisible((current)=>{const next=new Set(current);next.has(account._key)?next.delete(account._key):next.add(account._key);return next;})} title={show?"Ocultar":"Mostrar"}>{show?<EyeOff className="h-4 w-4"/>:<Eye className="h-4 w-4"/>}</Button><CopyButton value={account.password} label="Clave" onCopy={copy}/></div></div>
            </div>;
        })}
    </div>;
}

function CopyButton({ value, label, onCopy }) {
    return <Button type="button" variant="outline" size="icon" disabled={!value} onClick={()=>onCopy(value,label)} title={`Copiar ${label.toLowerCase()}`}><Copy className="h-4 w-4"/></Button>;
}
